/**
 * CortisolIntelCard — „Кортизолова интелигентност" в стресовия близнак.
 * Два симптомни профила, дневник на ритъма (слюнка в 4 точки или кръвен
 * сутрин), разчитане на кривата, 5 стъпки и намеси с числа.
 * Данни: GET /api/cortisol/overview · POST /symptoms · POST /rhythm
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, ChevronDown, Loader2, AlertTriangle, CheckCircle2, Info,
} from "lucide-react";
import { useAuth } from "@/App";

const POINTS = [
  ["wake", "при събуждане"],
  ["noon", "обед"],
  ["afternoon", "следобед"],
  ["evening", "преди сън"],
];

const PROFILE_STYLE = {
  high: "bg-orange-500/20 text-orange-600",
  low: "bg-sky-500/20 text-sky-600",
  mixed: "bg-violet-500/20 text-violet-600",
  few: "bg-amber-500/15 text-amber-600",
  calm: "bg-emerald-500/15 text-emerald-600",
};

export default function CortisolIntelCard({ isDark = false }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(true);
  const [form, setForm] = useState({ date: "", wake: "", noon: "", afternoon: "", evening: "", serum_am: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cortisol/overview`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const toggle = async (group, key) => {
    const cur = d.selected[group] || [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    const payload = { ...d.selected, [group]: next };
    setD({ ...d, selected: payload });
    try {
      const { data } = await axios.post(`${API}/cortisol/symptoms`, payload);
      setD(data);
    } catch { toast.error("Неуспешно"); }
  };

  const saveRhythm = async () => {
    if (!form.date) { toast.error("Изберете дата"); return; }
    setSaving(true);
    try {
      const body = { date: form.date };
      ["wake", "noon", "afternoon", "evening", "serum_am"].forEach((k) => {
        if (form[k] !== "") body[k] = Number(String(form[k]).replace(",", "."));
      });
      const { data } = await axios.post(`${API}/cortisol/rhythm`, body);
      setD(data);
      setForm({ date: "", wake: "", noon: "", afternoon: "", evening: "", serum_am: "" });
      toast.success("Записано — кривата е разчетена");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Записването не успя");
    } finally { setSaving(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const field = `w-full px-2 py-1.5 rounded-lg text-[12px] border ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  return (
    <div className={`rounded-2xl border p-4 mb-4 ${card}`} data-testid="cortisol-card">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="cortisol-toggle">
        <span className="text-[13px] font-extrabold inline-flex items-center gap-1.5">
          <Activity className="w-4 h-4 text-orange-500" /> Кортизолова интелигентност
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[11px] font-bold
                      ${PROFILE_STYLE[d.profile.key]}`} data-testid="cortisol-profile">
        {d.profile.label_bg} · {d.profile.high_count} висок / {d.profile.low_count} нисък
      </div>
      <div className="text-[11px] opacity-80 mt-1">{d.profile.note_bg}</div>

      {open && (
        <div className="mt-3 space-y-4 text-[11px]" data-testid="cortisol-body">
          {/* 5 стъпки */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="cortisol-steps">
            {d.steps.map((s) => (
              <div key={s.n}
                className={`p-2 rounded-xl border ${s.active
                  ? "border-orange-500 bg-orange-500/10"
                  : (isDark ? "border-white/10" : "border-slate-200")}`}>
                <div className="font-bold">{s.n}. {s.title_bg}</div>
                <div className="opacity-70 text-[10px] mt-0.5">{s.desc_bg}</div>
              </div>
            ))}
          </div>

          {/* симптоми */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["high", "Признаци на висок кортизол"], ["low", "Признаци на нисък кортизол"]]
              .map(([g, label]) => (
                <div key={g} data-testid={`cortisol-symptoms-${g}`}>
                  <div className="text-[10px] font-bold uppercase opacity-60 mb-1">{label}</div>
                  <div className="space-y-1">
                    {d.catalog[g].map((s) => (
                      <label key={s.key} className="flex items-start gap-1.5 cursor-pointer">
                        <input type="checkbox" className="mt-0.5 accent-orange-500"
                          checked={(d.selected[g] || []).includes(s.key)}
                          onChange={() => toggle(g, s.key)}
                          data-testid={`cortisol-sym-${s.key}`} />
                        <span>{s.label_bg}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
          </div>

          {/* дневник на ритъма */}
          <div className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-slate-200"}`}
            data-testid="cortisol-rhythm-form">
            <div className="text-[10px] font-bold uppercase opacity-60">
              Дневник на ритъма — слюнка (ng/mL) или кръвен сутрин (µg/dL)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 mt-1.5">
              <label className="block col-span-2 sm:col-span-1">
                <span className="text-[10px] opacity-60">дата</span>
                <input type="date" value={form.date} className={field}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="cortisol-date" />
              </label>
              {POINTS.map(([k, l]) => (
                <label key={k} className="block">
                  <span className="text-[10px] opacity-60">{l}</span>
                  <input value={form[k]} inputMode="decimal" className={field}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`cortisol-point-${k}`} />
                </label>
              ))}
              <label className="block">
                <span className="text-[10px] opacity-60">кръвен AM</span>
                <input value={form.serum_am} inputMode="decimal" className={field}
                  onChange={(e) => setForm({ ...form, serum_am: e.target.value })}
                  data-testid="cortisol-serum" />
              </label>
            </div>
            <button onClick={saveRhythm} disabled={saving}
              className="mt-2 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white
                         bg-gradient-to-r from-orange-500 to-amber-600 disabled:opacity-50
                         inline-flex items-center gap-1.5"
              data-testid="cortisol-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Запази
            </button>

            {d.latest && (
              <div className="mt-2 space-y-1" data-testid="cortisol-reading">
                <div className="text-[10px] font-bold uppercase opacity-60">
                  Разчитане от {d.latest_date}
                </div>
                {d.latest.findings_bg.map((f, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    {d.latest.pattern === "normal"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />}
                    <span>{f}</span>
                  </div>
                ))}
                <div className="opacity-60 text-[10px]">{d.latest.note_bg}</div>
              </div>
            )}
          </div>

          {/* намеси */}
          <div data-testid="cortisol-interventions">
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">
              Какво работи — с числа
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {d.interventions.map((i) => (
                <div key={i.key}
                  className={`p-2.5 rounded-xl border ${isDark ? "border-white/10" : "border-slate-200"}`}
                  data-testid={`cortisol-int-${i.key}`}>
                  <div className="font-bold">{i.title_bg}</div>
                  <div className="opacity-85 mt-0.5">{i.how_bg}</div>
                  <div className="opacity-60 text-[10px] mt-0.5">{i.why_bg}</div>
                </div>
              ))}
            </div>
          </div>

          {/* връзки към другите модули */}
          <div data-testid="cortisol-cross-links">
            <div className="text-[10px] font-bold uppercase opacity-60 mb-1">
              Какво дърпа кортизолът след себе си
            </div>
            {d.cross_links_bg.map((c, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 text-cyan-500 shrink-0 mt-0.5" />
                <span>{c}</span>
              </div>
            ))}
          </div>

          <div className="text-[9px] opacity-50">{d.disclaimer_bg}</div>
        </div>
      )}
    </div>
  );
}
